import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useExcelDemo } from '../hooks/useExcelDemo';
import { excelFormSchema, type ExcelFormValues } from '../schema/showcaseSchema';

export function ExcelPanel() {
  const { t } = useTranslation();
  const { mutate, isPending, isSuccess, isError, error, reset } = useExcelDemo();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm<ExcelFormValues>({
    resolver: zodResolver(excelFormSchema),
    defaultValues: { rows: 100 },
  });

  const rows = watch('rows');

  function onSubmit(values: ExcelFormValues) {
    mutate(
      { rows: values.rows },
      {
        onSuccess: (blob) => {
          // Trigger browser download
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `showcase-${values.rows}.xlsx`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        },
      }
    );
  }

  const isOverLimit = typeof rows === 'number' && rows > 10000;

  return (
    <section aria-labelledby="excel-panel-title">
      <h2
        id="excel-panel-title"
        style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          marginBottom: 'var(--spacing-4)',
          marginTop: 0,
        }}
      >
        {t('showcase.excel.title')}
      </h2>
      <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--spacing-6)' }}>
        {t('showcase.excel.subtitle')}
      </p>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div style={{ marginBottom: 'var(--spacing-4)' }}>
          <label
            htmlFor="excel-rows"
            style={{
              display: 'block',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              color: 'var(--color-text-secondary)',
              marginBottom: 'var(--spacing-2)',
            }}
          >
            {t('showcase.excel.rows')}
          </label>
          <input
            id="excel-rows"
            type="number"
            min={1}
            max={10000}
            aria-label={t('showcase.excel.rows')}
            aria-invalid={!!errors.rows || isOverLimit}
            aria-describedby={errors.rows ? 'excel-rows-error' : undefined}
            {...register('rows', { valueAsNumber: true })}
            disabled={isPending}
            style={{
              width: '100%',
              padding: 'var(--spacing-3)',
              border: `1px solid ${errors.rows ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontFamily: 'var(--font-family-mono)',
              background: isPending ? 'var(--color-surface-muted)' : 'var(--color-surface-background)',
              color: 'var(--color-text-primary)',
              boxSizing: 'border-box',
            }}
          />
          {errors.rows && (
            <p
              id="excel-rows-error"
              role="alert"
              style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)', marginTop: 'var(--spacing-1)' }}
            >
              {errors.rows.message}
            </p>
          )}
          {!errors.rows && typeof rows === 'number' && rows > 1000 && rows <= 10000 && (
            <p
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-warning)',
                marginTop: 'var(--spacing-1)',
              }}
            >
              {t('showcase.excel.streamingNote')}
            </p>
          )}
        </div>

        <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-6)' }}>
          <button
            type="submit"
            disabled={isPending || !isValid}
            aria-busy={isPending}
            style={{
              padding: 'var(--spacing-3) var(--spacing-6)',
              background: isPending ? 'var(--color-text-muted)' : 'var(--color-brand-primary)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: isPending ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-family-sans)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-2)',
            }}
          >
            {isPending && (
              <span
                aria-hidden="true"
                style={{
                  width: 14,
                  height: 14,
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTopColor: 'var(--color-text-inverse)',
                  borderRadius: '50%',
                  display: 'inline-block',
                  animation: 'spin 0.8s linear infinite',
                }}
              />
            )}
            {isPending ? t('showcase.excel.loading') : t('showcase.excel.download')}
          </button>
          {isSuccess && (
            <button
              type="button"
              onClick={() => reset()}
              style={{
                padding: 'var(--spacing-3) var(--spacing-4)',
                background: 'transparent',
                color: 'var(--color-text-secondary)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                cursor: 'pointer',
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              {t('showcase.common.reset')}
            </button>
          )}
        </div>
      </form>

      {/* Success state */}
      {isSuccess && (
        <div
          role="status"
          aria-live="polite"
          style={{
            border: '1px solid var(--color-success)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-4)',
            background: 'var(--color-surface-muted)',
            color: 'var(--color-success)',
            fontSize: 'var(--font-size-sm)',
            marginBottom: 'var(--spacing-4)',
          }}
        >
          ✓ {t('showcase.excel.success', { filename: `showcase-${rows}.xlsx` })}
        </div>
      )}

      {/* Error state (413 = rows exceeded) */}
      {isError && error && (
        <div
          role="alert"
          style={{
            border: '1px solid var(--color-danger)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-4)',
            background: 'var(--color-surface-muted)',
            fontSize: 'var(--font-size-sm)',
            marginBottom: 'var(--spacing-4)',
          }}
        >
          <strong style={{ color: 'var(--color-danger)' }}>{error.code}</strong>
          <p style={{ color: 'var(--color-text-primary)', margin: 'var(--spacing-2) 0 0' }}>
            {error.code === 'SHOWCASE-413-001'
              ? t('showcase.excel.error.tooMany')
              : error.message}
          </p>
        </div>
      )}

      {/* Info box */}
      <div
        style={{
          padding: 'var(--spacing-3)',
          background: 'var(--color-surface-elevated)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-secondary)',
        }}
      >
        <span aria-hidden="true" style={{ marginRight: 'var(--spacing-2)' }}>📚</span>
        <strong>{t('showcase.panel.learning')}</strong> {t('showcase.excel.streamingNote')}
      </div>
    </section>
  );
}
